import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================

/**
 * Pet Policy page intro: centered heading and blurb that opens the page
 * below the hero — same pattern as FloorPlansIntro / VirtualTourIntro.
 */
export function PetPolicyIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-6 md:pb-8"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="petpolicyintro.heading"
          label="Heading"
          defaultValue="<p>Pet-Friendly Living</p>"
          as="h2"
          className="text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.1] mb-6"
          style={{ color: '#000', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        />

        <div className="max-w-3xl mx-auto">
          <RichTextContent
            path="petpolicyintro.body"
            label="Body Text"
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyIntro',
  displayName: 'Pet Policy Intro',
  tokens: {
    'petpolicyintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Pet-Friendly Living</p>',
    },
    'petpolicyintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_pets_blurb',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we know pets are family. Review our pet policy below for deposits, fees, and guidelines so you and your furry companions can settle in with confidence.</p>',
    },
  },
};

(PetPolicyIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicyIntro;
